import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import { connect } from 'react-redux';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { Link } from 'site-modules/shared/components/link/link';
import { LeadFormDrawer } from 'site-modules/shared/components/lead-form/lead-form-drawer/lead-form-drawer';
import { DrawerV2 } from 'site-modules/shared/components/drawer/drawer-v2';
import { WhiteBackButton } from 'site-modules/shared/components/lead-form/lead-form-drawer/close-buttons/white-back-button/white-back-button';
import { LeaseDealsLeadForm } from 'site-modules/shared/components/lead-form/unified-lead-form-pages/lease-deals-lead-form';
import { formatPriceStringV2 } from 'client/site-modules/shared/utils/price-utils';

export function LeaseDealsCtaUI({
  showCta = false,
  styleId,
  make,
  model,
  year,
  trim = '',
  payment,
  term,
  dueAtSigning,
  endDate,
  disclaimer = '',
  webUrls = '',
  photo = undefined,
  errorImage = '',
  pageName,
  buttonProps,
  formName,
}) {
  const [isDrawerOpened, toggleDrawerOpened] = useToggle(false);

  const handleButtonClick = useCallback(() => {
    toggleDrawerOpened();
    buttonProps.onClick?.();
  }, [buttonProps, toggleDrawerOpened]);

  if (!showCta) {
    return (
      <Button
        tag={Link}
        to={`/${makeNiceName(make)}-${makeNiceName(model)}-lease-deals`}
        color="success"
        size="lg"
        data-tracking-id="view_search_result"
        rel="nofollow"
        {...buttonProps}
      >
        <span>See available inventory</span>
      </Button>
    );
  }

  const dataTrackingLeadForm = getLeadFormCreativeId({
    isNewVin: true,
    formName,
    pageNameLegacy: pageName,
  });

  return (
    <>
      <Button
        color="success"
        size="lg"
        data-tracking-id="lead_form_button"
        aria-label={`Get This Deal: ${formatPriceStringV2(payment)} per month ${year} ${make} ${model}`}
        {...buttonProps}
        onClick={handleButtonClick}
      >
        Get this deal
      </Button>

      <LeadFormDrawer
        url=""
        isOpen={isDrawerOpened}
        onDrawerToggle={toggleDrawerOpened}
        drawerComponent={DrawerV2}
        containerClassName="pos-st"
        drawerContentClassName="bg-cool-gray-90"
        closeBtnComponent={WhiteBackButton}
        dataTrackingParent={dataTrackingLeadForm}
        component={LeaseDealsLeadForm}
        componentData={{
          options: {
            creativeId: dataTrackingLeadForm,
          },
          params: {
            styleId,
            make,
            model,
            year,
            trim,
            payment,
            term,
            dueAtSigning,
            endDate,
            disclaimer,
            webUrls,
            photo,
            errorImage,
          },
        }}
      />
    </>
  );
}

LeaseDealsCtaUI.propTypes = {
  showCta: PropTypes.bool,
  styleId: PropTypes.number.isRequired,
  make: PropTypes.string.isRequired,
  model: PropTypes.string.isRequired,
  year: PropTypes.number.isRequired,
  trim: PropTypes.string,
  payment: PropTypes.number.isRequired,
  term: PropTypes.number.isRequired,
  dueAtSigning: PropTypes.number.isRequired,
  endDate: PropTypes.string.isRequired,
  disclaimer: PropTypes.string,
  webUrls: PropTypes.string,
  photo: PropTypes.shape({}),
  errorImage: PropTypes.string,
  pageName: PropTypes.string.isRequired,
  buttonProps: PropTypes.shape({
    className: PropTypes.string,
    onClick: PropTypes.func,
  }).isRequired,
  formName: PropTypes.string.isRequired,
};

const mapStateToProps = state => ({
  pageName: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
});

export const LeaseDealsCta = connect(mapStateToProps)(LeaseDealsCtaUI);
